/**
 * Transform and dirty region management utilities
 */

import type { CanvasViewport, DirtyRegion, TransformState } from './types'

/**
 * Calculate dirty regions based on viewport changes
 */
export function calculateDirtyRegions(
  oldViewport: CanvasViewport,
  newViewport: CanvasViewport
): DirtyRegion[] {
  const regions: DirtyRegion[] = []

  // If scale changed, entire viewport is dirty (caller handles this)
  if (oldViewport.scale !== newViewport.scale) {
    return [] // Signal to caller that full redraw is needed
  }

  // Calculate the delta movement
  const deltaX = newViewport.x - oldViewport.x
  const deltaY = newViewport.y - oldViewport.y

  // If no movement, no dirty regions
  if (deltaX === 0 && deltaY === 0) {
    return regions
  }

  // Horizontal movement - exposed vertical strips
  if (deltaX !== 0) {
    regions.push({
      x: deltaX > 0 ? oldViewport.x + oldViewport.width : newViewport.x,
      y: Math.min(oldViewport.y, newViewport.y),
      width: Math.abs(deltaX),
      height: Math.max(oldViewport.height, newViewport.height)
    })
  }

  // Vertical movement - exposed horizontal strips
  if (deltaY !== 0) {
    regions.push({
      x: Math.min(oldViewport.x, newViewport.x),
      y: deltaY > 0 ? oldViewport.y + oldViewport.height : newViewport.y,
      width: Math.max(oldViewport.width, newViewport.width),
      height: Math.abs(deltaY)
    })
  }

  return regions
}

/**
 * Create a dirty region covering the entire canvas
 */
export function createFullCanvasDirtyRegion(width: number, height: number): DirtyRegion {
  return { x: 0, y: 0, width, height }
}

/**
 * Transform a region from world space to canvas space
 */
export function transformRegionToCanvas(
  region: DirtyRegion,
  transform: TransformState
): DirtyRegion {
  return {
    x: region.x * transform.scale + transform.translateX,
    y: region.y * transform.scale + transform.translateY,
    width: region.width * transform.scale,
    height: region.height * transform.scale
  }
}

/**
 * Transform screen coordinates to world space
 */
export function transformPointToWorld(
  x: number,
  y: number,
  viewport: CanvasViewport
): { x: number; y: number } {
  return {
    x: (x - viewport.x) / viewport.scale,
    y: (y - viewport.y) / viewport.scale
  }
}
